/* webComponentOnsiteSearch component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, Event, EventEmitter, h, Listen, Method, Prop, State, Watch } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import OnsiteSearchComponentTemplate from './templates/cwc-onsite-search.template';
import { AbstractComponent } from '@cewe-designsystem/abstract';
import { OnsiteSearchState } from './onsite-search-state.enum';
import { OnsiteSearchConfiguration, OnsiteSearchTextResources } from './config/onsite-search-configuration';
import { SearchClient } from './services/search-client';
import UtilBreakpoints from '@cewe-designsystem/util_breakpoints';
import ScrollableElement from '../../../interfaces/ScrollableElement';
import sessionStore from './session-store';
import localStore from './local-store';
import { OnsiteSearchDefaultConfiguration } from './config/onsite-search-default-configuration';
import { OnsiteSearchDefaultTextResources } from './config/onsite-search-default-text-resources';
import {
  TrackingQuery,
  TrackingResult,
  TrackingResultCategoryClick,
  TrackingResultClick,
  TrackingResultFilterClick,
  TrackingResultMoreClick,
  TrackingResultStats,
  TrackingSuggestClick,
} from './tracking';
import { SearchQueryType } from './search-query-type.enum';
import deepmerge from 'deepmerge';
import { OnSiteSearchModule } from './modules/onsite-search-module';
import { OnsiteSearchModuleCollection } from './modules/onsite-search-module-collection';
import { OrderStatusOnSiteSearchModule } from './modules/order-status/order-status-onsite-search-module';
import { OnSiteSearchModuleConfigurations } from './modules/onsite-search-module-configurations';
import { BaseIndexItem, SchemaName } from '@cewe-onsitesearch/schemas';
import { TransformedQuery } from './transformer/transformed-query';
import { QueryTransformerChain } from './transformer/query-transformer-chain';
import { FormatQueryTransformer } from './transformer/format-query-transformer';
import { DelayTimeCalculator } from './services/delay-time-calculator';
import { SearchResponse } from './model/search-response.model';
import { InterfaceSearchProviderConfig } from '@cewe-designsystem/util_onsitesearch-api-client/dist/InterfaceSearchProviderConfig';
import { MockdataSearchProvider } from '@cewe-designsystem/util_onsitesearch-api-client/dist/MockdataSearchProvider';
import { MockdataSearchProviderConfig } from '@cewe-designsystem/util_onsitesearch-api-client/dist/MockdataSearchProviderConfig';
import { CeweSearchProvider } from '@cewe-designsystem/util_onsitesearch-api-client/dist/CeweSearchProvider';
import { CeweSearchClientConfig } from '@cewe-designsystem/util_onsitesearch-api-client/dist/model/CeweSearchClientConfig';
import { InterfaceSearchProvider } from '@cewe-designsystem/util_onsitesearch-api-client/dist/InterfaceSearchProvider';

/**
 * @TODO:
 * - scrollStop for body!!
 *
 * @slot close-icon - HTML element used as caption for the button to close the search overlay in desktop mode e.g. an img or svg
 * @slot mobile-close-icon - HTML element used as caption for the button to close the search overlay in mobile mode e.g. an img or svg
 */

@Component({
  tag: 'cwc-onsite-search',
  styleUrl: './styles/cwc-onsite-search.scss',
  shadow: true,
})
export class OnsiteSearchComponent implements ScrollableElement {
  /**
   * name of the parameter added to URLs after redirecting via search result.
   */
  static referrerMarker = '_oss';

  /**
   * the DOM Host Element
   */
  @Element()
  element: HTMLElement;

  /**
   * indicates if the search is opened and in use or is closed.
   *
   * this state is used for styling.
   */
  @Prop({ reflect: true, mutable: true })
  state: string = OnsiteSearchState.Closed;

  /**
   * show visible state of search component.
   */
  @State()
  visible: boolean = false;

  /**
   * loading indicator visibility.
   */
  @State()
  loading: boolean = false;
  /**
   * TextResources object with translations.
   */
  @Prop({ attribute: 'text-resources' }) _textResources: OnsiteSearchTextResources | string;
  /**
   * Classname of the default search provider.
   */
  @Prop()
  defaultProvider: string;
  /**
   * property / attribute to inject defaultSearchProviderConfig via markup.
   */
  @Prop({ attribute: 'default-provider-config' }) _defaultProviderConfig: CeweSearchClientConfig | string;
  /**
   *
   */
  @Prop({ attribute: 'module-config' }) moduleConfigs: string = '';
  /**
   * latest SearchResponse, will be set async after a new search req.
   * This variable is used to allow automatic rendering if new data received.
   */
  @State()
  searchResponse: SearchResponse;
  /**
   * the compoundID of the previous SearchResponse
   * Used when deciding whether the overlay should scroll to the top for new results. Will be updated afterward
   */
  oldSearchResponseID: string;
  /**
   * Type of the last search query.
   * Shows if the search was started by user (organic) or via app based actions like suggestions.
   */
  searchQueryType: SearchQueryType;
  /**
   * The original query, before transformation. Used in tracking and displaying when no result has been found for it.
   */
  originalSearchQuery: string;
  /**
   * collection of possible search modules.
   */
  modules: OnsiteSearchModuleCollection;
  /**
   * Chain of QueryTransformers to manipulate the input searchPhrase for searches.
   */
  queryTransformer: QueryTransformerChain = new QueryTransformerChain();
  /**
   * active search module.
   */
  activeModule: OnSiteSearchModule;
  /**
   * Event thrown if the searchbox becomes visible
   *
   * @docs
   * @event on:onsite-search:visible
   */
  @Event({
    eventName: 'on:onsite-search:visible',
  })
  onSearchVisibleEvent: EventEmitter;
  /**
   * Event thrown if the searchbox gets the focus
   *
   * @docs
   * @event on:onsite-search:focus
   */
  @Event({
    eventName: 'on:onsite-search:focus',
  })
  onSearchFocusEvent: EventEmitter;
  /**
   * Event thrown if the searchbox losts the focus
   *
   * @docs
   * @event on:onsite-search:blur
   */
  @Event({
    eventName: 'on:onsite-search:blur',
  })
  onSearchBlurEvent: EventEmitter;
  /**
   * Event thrown if the search is closed
   *
   * @docs
   * @event on:onsite-search:close
   */
  @Event({
    eventName: 'on:onsite-search:close',
  })
  onSearchCloseEvent: EventEmitter;
  /**
   * Event thrown if the search begins to show results
   *
   * @docs
   * @event on:onsite-search:show
   */
  @Event({
    eventName: 'on:onsite-search:show',
  })
  onSearchShowEvent: EventEmitter;
  /**
   * Event thrown if an item is selected
   *
   * @docs
   * @event on:onsite-search:itemSelect
   */
  @Event({
    eventName: 'on:onsite-search:itemSelect',
  })
  onSearchItemSelectEvent: EventEmitter;
  /**
   * Event thrown if a query suggestion item is selected
   *
   * @docs
   * @event on:onsite-search:suggestionSelect
   */
  @Event({
    eventName: 'on:onsite-search:suggestionSelect',
  })
  onQuerySuggestionSelectEvent: EventEmitter;
  /**
   * Event thrown if the user clicks on "show more results"
   *
   * @docs
   * @event on:onsite-search:showMoreResults
   */
  @Event({
    eventName: 'on:onsite-search:showMoreResults',
  })
  onSearchShowMoreResultsEvent: EventEmitter;
  /**
   * Event thrown if the user filters the search results.
   *
   * @docs
   * @event on:onsite-search:resultFilterClick
   */
  @Event({
    eventName: 'on:onsite-search:resultFilterClick',
  })
  onSearchResultFilterClickEvent: EventEmitter;
  /**
   * Event thrown if the user filters the search results.
   *
   * @docs
   * @event on:onsite-search:resultCategoryClick
   */
  @Event({
    eventName: 'on:onsite-search:resultCategoryClick',
  })
  onSearchResultCategoryClickEvent: EventEmitter;
  /**
   * Event thrown if the search finds some results.
   *
   * @docs
   * @event on:onsite-search:resultsFound
   */
  @Event({
    eventName: 'on:onsite-search:resultsFound',
  })
  onSearchResultsFoundEvent: EventEmitter;
  /**
   * Event thrown if the search does not find any results.
   *
   * @docs
   * @event on:onsite-search:resultsFound
   */
  @Event({
    eventName: 'on:onsite-search:resultsNotFound',
  })
  onSearchResultsNotFoundEvent: EventEmitter;

  /**
   * text resources for translations.
   */
  textResources: OnsiteSearchTextResources = OnsiteSearchDefaultTextResources;

  /**
   * Configuration of the component.
   */
  configuration: OnsiteSearchConfiguration = OnsiteSearchDefaultConfiguration;

  /**
   * handler for breakpoints in css.
   */
  breakPoints: UtilBreakpoints;

  /**
   * config settings of the default search provider.
   * @private
   */
  private defaultProviderConfig: InterfaceSearchProviderConfig = {};

  /**
   * Time for delaying the loading indicator.
   * @private
   */
  private loadingTimer: ReturnType<typeof setTimeout>;
  /**
   * Reference of the injected input field for search queries.
   */
  private searchInput: Element;
  /**
   * used searchClient.
   */
  private searchClient: SearchClient;

  /**
   * Timer for delayed search requests
   */
  private inputTimer: ReturnType<typeof setTimeout>;

  /**
   * Timer for delayed tracking events
   * @private
   */
  private trackingTimer: ReturnType<typeof setTimeout>;

  /**
   * is the session state already handled after load.
   * @private
   */
  private sessionStateHandled: boolean = false;

  /**
   * a calculator for the debounce time
   * @private
   */
  private delayTimeCalculator: DelayTimeCalculator;

  /**
   * @constructor
   */
  constructor() {
    this.queryTransformer.add(new FormatQueryTransformer());

    this.modules = new OnsiteSearchModuleCollection([OrderStatusOnSiteSearchModule]);
  }

  /**
   * Called every time the component is connected to the DOM.
   * When the component is first connected, this method is called before componentWillLoad.
   * It is important to note that this method can be called more than once, everytime, the element is attached or moved in the DOM.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec
   */
  connectedCallback() {
    // init searchClient and link ready state to visibility of component
    // this.searchClient = new SearchClient(new MG215
    // ockdataSearchProvider());
    this.setDefaultProviderConfig(this._defaultProviderConfig);
    this.setTextResourcesConfig(this._textResources);

    this.delayTimeCalculator = new DelayTimeCalculator(
      this.configuration.searchInputDelay,
      this.configuration.searchInputDelay * 2,
    );

    this.breakPoints = new UtilBreakpoints();

    let provider: InterfaceSearchProvider;
    if (this.defaultProvider === 'MockdataSearchProvider') {
      provider = new MockdataSearchProvider(this.defaultProviderConfig as MockdataSearchProviderConfig);
    } else {
      provider = new CeweSearchProvider(this.defaultProviderConfig as CeweSearchClientConfig, localStore);
    }

    this.searchClient = new SearchClient(provider); // @TODO: outsource -> maybe factory
    this.searchClient
      .isReady()
      .then((ready) => {
        // if API becomes ready, component can be shown
        if (!ready && sessionStore.shown) {
          // show if not ready but seen before
          this.visible = true;
          sessionStore.shown = true;
        } else {
          this.visible = ready;
          sessionStore.shown = ready;
        }
      })
      .catch(() => {
        if (sessionStore.shown) {
          // show if not ready but seen before
          this.visible = true;
          sessionStore.shown = true;
        } else {
          this.visible = false;
          sessionStore.shown = false;
        }
      });
  }

  /**
   * @inheritDoc
   */
  @Method()
  getScrollableElement(): Promise<HTMLElement> {
    return new Promise((resolve) => resolve(this.getContent().querySelector<HTMLElement>('overlay-content')));
  }

  /**
   * handle focusin event and open search.
   */
  @Listen('focusin', { passive: true })
  onFocus(ev) {
    if (this.hasMinSearchValueLength()) {
      if (!this.searchResponse) {
        this.search(ev.target.value, SearchQueryType.ORGANIC);
      } else {
        this.open();
      }
    } else {
      this.onSearchFocusEvent.emit(this.element);
    }
  }

  /**
   * handle focusout (lost).
   */
  @Listen('focusout', { passive: true })
  onBlur() {
    this.onSearchBlurEvent.emit(this.element);

    this.delayTimeCalculator.reset();
  }

  /**
   * handle input events of the input element for search queries.
   * @param ev
   */
  @Listen('input', { passive: true })
  onTyping(ev) {
    this.delayTimeCalculator.update();

    clearTimeout(this.trackingTimer);

    clearTimeout(this.inputTimer);

    if (!this.hasMinSearchValueLength() || this.searchInput != ev.target) {
      return;
    }

    this.inputTimer = setTimeout(() => {
      this.search(ev.target.value, SearchQueryType.ORGANIC);
    }, this.configuration.searchInputDelay);
  }

  /**
   * handle keyboard events.
   * @param ev
   */
  @Listen('keydown', { target: 'window' })
  onKeyDown(ev: KeyboardEvent) {
    if (this.state !== OnsiteSearchState.Opened) {
      return;
    }

    switch (ev.key) {
      case 'Escape':
        this.close(false);
        break;
    }
  }

  /**
   * watch text-resource attribute and convert json string to object.
   * @param newValue
   */
  @Watch('_textResources')
  setTextResourcesConfig(newValue: OnsiteSearchTextResources | string) {
    let passedConfig;

    if (typeof newValue === 'string') {
      try {
        passedConfig = JSON.parse(newValue);
      } catch (e) {
        console.error('invalid text resources, using defaults', newValue, e);
      }
    } else {
      passedConfig = newValue;
    }

    this.textResources = deepmerge(OnsiteSearchDefaultTextResources, passedConfig ?? {});
  }

  /**
   * watch default-provider-config attribute and convert json string to object.
   * @param newValue
   */
  @Watch('_defaultProviderConfig')
  setDefaultProviderConfig(newValue: CeweSearchClientConfig | string) {
    if (typeof newValue === 'string') {
      this.defaultProviderConfig = { ...this.defaultProviderConfig, ...JSON.parse(newValue) };
    } else {
      this.defaultProviderConfig = { ...this.defaultProviderConfig, ...newValue };
    }
  }

  getDefaultProviderConfig(): InterfaceSearchProviderConfig {
    return this.defaultProviderConfig;
  }

  /**
   * returns the parsed module configurations.
   * @return OnSiteSearchModuleConfigurations
   */
  getModuleConfigs(): OnSiteSearchModuleConfigurations {
    let moduleConfigs: OnSiteSearchModuleConfigurations = {};
    try {
      moduleConfigs = JSON.parse(this.moduleConfigs) as OnSiteSearchModuleConfigurations;
    } catch (e) {
      console.error('can not parse module configs string', this.moduleConfigs, e);
    }

    return moduleConfigs;
  }

  /**
   * start a new search request
   * @param searchPhrase
   * @param type
   */
  @Method()
  async search(searchPhrase: string, type?: SearchQueryType) {
    this.showLoading(500); /* eslint-disable-line no-magic-numbers */
    try {
      const moduleConfigs = this.getModuleConfigs();

      // find module to handle search req.
      this.activeModule = this.modules.getByQuery(searchPhrase, moduleConfigs);

      if (this.activeModule) {
        this.searchResponse = null;
      } else {
        // fallback default search
        this.originalSearchQuery = searchPhrase.trim();
        const transformedSearchPhrase = this.queryTransformer.transform(new TransformedQuery(searchPhrase)).toString();
        this.searchResponse = await this.searchClient.search(transformedSearchPhrase, {
          indices: [
            SchemaName.main,
            SchemaName.product,
            SchemaName.category,
            SchemaName.inspiration,
            SchemaName.service,
            SchemaName.page,
          ],
        });
        if (type) {
          this.searchQueryType = type;
        }

        this.trackingTimer = setTimeout(() => {
          this.trackResults(this.searchResponse, this.searchQueryType);
        }, this.delayTimeCalculator.getDelayTime()); //
      }
    } catch (e) {
      this.searchResponse = null;
      this.searchQueryType = null;
      console.error(e);
    } finally {
      this.hideLoading();
      this.open();
    }
  }

  /**
   * close the search overlay and optionally clear the entered search phrase.
   */
  @Method()
  async close(clear: boolean = false) {
    this.state = OnsiteSearchState.Closed;
    this.onSearchCloseEvent.emit(this.element);
    document.querySelector('body').style.removeProperty('overflow');

    if (clear) {
      this.setInputValue('');
    }
  }

  @Watch('visible')
  componentChangeVisibility(newValue: boolean, oldValue: boolean) {
    if (newValue === true && oldValue === false) {
      this.onSearchVisibleEvent.emit();
    }
  }

  /**
   * deactivate currently active jumplink on new query
   */
  @Watch('searchResponse')
  resetJumplinks() {
    const activeJumpLink = this.getContent().querySelector<HTMLElement>(`[data-active]`)?.dataset.tabname;
    this.handleJumplinks(false, activeJumpLink);
    this.scrollToTop('smooth');
  }

  /**
   * Called every time the component is disconnected from the DOM, i.e., it calls be dispatched more than once
   * DO not confuse with a "onDestroy" kind of event.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec.
   */
  disconnectedCallback() {}

  /**
   * Called once just after the component is first connected to the DOM.
   * A promise can be returned, that can be used to wait for the first render.
   */
  componentWillLoad() {}

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {}

  /**
   * Called before every render().
   * A promise can be returned, that can be used to wait for the upcoming render.
   */
  componentWillRender() {
    if (this.searchResponse) {
      this.scrollOnResultChanges();
    }
  }

  /**
   * Called after every render().
   */
  componentDidRender() {
    const slot = this.element.shadowRoot.querySelector('slot[name="input"]');
    if (slot instanceof HTMLSlotElement) {
      this.searchInput = slot.assignedElements()[0];
      this.handleSessionState();
    }

    //persistence of active jumplink on closing and opening search overlay
    const activeJumpLink = this.getContent().querySelector<HTMLElement>(`[data-active]`)?.dataset.tabname;
    this.handleJumplinks(true, activeJumpLink);

    this.enableKeyboardSelectNextItem(this.getContent().querySelectorAll('result-item'));
  }

  /**
   * Called when the component is about to be updated because some Prop() or State() changed.
   * It is never called during the first render().
   * A promise can be returned, that can be used to wait for the next render.
   */
  componentWillUpdate() {}

  /**
   * Called just after the component updates. It is never called during the first render().
   */
  componentDidUpdate() {}

  /**
   * @inheritDoc
   */
  render() {
    if (!this.visible) {
      return '';
    }

    return <OnsiteSearchComponentTemplate controller={this} />;
  }

  /**
   * click listener for show more button in search results.
   */
  onShowMoreResults(listType: SchemaName) {
    const data: TrackingResultMoreClick = { query: this.searchResponse.request.query, listType };

    this.onSearchShowMoreResultsEvent.emit(data);

    return false;
  }

  /**
   * handles persistence, activation and deactivation of jumplinks
   * if demanded, this method will keep the previously active jumplink active
   * @param keepActiveJumplink
   * @param listType
   */
  handleJumplinks(keepActiveJumplink: boolean, listType: string) {
    if (listType) {
      const jumpLinks = this.getContent().querySelector<HTMLElement>('jump-links');
      const tag = jumpLinks.querySelector<HTMLElement>(`[data-tabname='${listType}']`);

      if (keepActiveJumplink) {
        delete this.getContent().querySelector<HTMLElement>(`[data-tabname='${listType}']`).dataset.active;
      }

      //disable other jumplinks
      jumpLinks
        .querySelectorAll(`[data-active]:not([data-tabname='${listType}'])`)
        .forEach((tag: HTMLElement) => delete tag.dataset.active);
      //activate selected jumplink
      if (Object.prototype.hasOwnProperty.call(tag.dataset, 'active')) {
        delete tag.dataset.active;
      } else {
        tag.dataset.active = '';
      }

      //hide non-selected sections
      const activeTags = jumpLinks.querySelectorAll('[data-active]');
      jumpLinks.querySelectorAll('*').forEach((tag: HTMLElement) => {
        const list = this.getContent().querySelector<HTMLElement>(`results[type='${tag.dataset.tabname}']`);
        if (list) {
          list.dataset.visible =
            activeTags.length === 0 || Object.prototype.hasOwnProperty.call(tag.dataset, 'active') ? 'true' : 'false';
          list.querySelector<HTMLFormElement>('.more-results-toggle').checked = activeTags.length === 1; //expand if only one tab is active
        }
      });
    }
  }

  /**
   * click listener for resultList tags to filter results.
   */
  onTagClick(listType: SchemaName) {
    const tag = this.getContent()
      .querySelector<HTMLElement>('jump-links')
      .querySelector<HTMLElement>(`[data-tabname='${listType}']`);

    this.handleJumplinks(false, listType);

    //track click
    const data: TrackingResultFilterClick = {
      listType: listType,
      active: Object.prototype.hasOwnProperty.call(tag.dataset, 'active'),
    };
    this.onSearchResultFilterClickEvent.emit(data);

    this.scrollToTop('smooth');
  }

  /**
   * click listener for retry button.
   */
  onRestartSearch() {
    if (this.searchInput) {
      this.search(this.searchInput['value']);
    }
  }

  /**
   * click listener for selecting a query suggestion.
   */
  onSelectQuerySuggestion(suggestItem: BaseIndexItem, position: number) {
    const data: TrackingSuggestClick = {
      item: suggestItem,
      position,
      query: this.searchResponse.request.query,
    };
    this.onQuerySuggestionSelectEvent.emit(data);

    if (this.searchInput) {
      this.searchInput['value'] = suggestItem.name;
      this.search(this.searchInput['value'], SearchQueryType.SUGGESTED);
    }
  }

  /**
   * open the link of a selected item.
   * @param selectedItem
   */
  async onSelectItem(selectedItem, position: number, queryId: string, listType: SchemaName) {
    // @TODO: find a better type
    try {
      await this.trackSelectItem(queryId, selectedItem, position, listType); // @TODO: add timeout of 250ms if request lacks
    } finally {
      // save search query in session
      sessionStore.lastSearchQuery = this.searchResponse.request.query;

      let linkUrl: string = '';
      if (selectedItem.type == 'product') {
        linkUrl = selectedItem.links.pip;
      } else {
        linkUrl = selectedItem.links.page;
      }
      const url = new URL(linkUrl);
      url.searchParams.set(OnsiteSearchComponent.referrerMarker, '1');

      if (window.location.pathname == url.pathname && url.hash !== '') {
        // close search if stay on same page with anchor
        await this.close(false);
        location.href = url.toString();
      } else {
        location.href = url.toString();
      }
    }
  }

  /**
   * open the category link of an item.
   * @param event
   * @param selectedItem
   * @param position
   * @param listType
   */
  async onCategoryClick(event: Event, selectedItem: BaseIndexItem, position: number, listType: SchemaName) {
    event.stopPropagation();

    try {
      // track click
      const data: TrackingResultCategoryClick = {
        flag: this.searchQueryType,
        query: this.searchResponse.request.query,
        item: selectedItem,
        position,
        listType,
      };
      this.onSearchResultCategoryClickEvent.emit(data);
    } finally {
      const url = new URL(selectedItem.links.category);
      url.searchParams.set(OnsiteSearchComponent.referrerMarker, '1');
      location.href = url.toString();
    }
  }

  /**
   * tracks the click on a result item.
   * @param queryID
   * @param selectedItem
   * @param position
   */
  async trackSelectItem(queryID: string, selectedItem, position: number, listType: SchemaName): Promise<boolean> {
    const data: TrackingResultClick = {
      flag: this.searchQueryType,
      query: this.searchResponse.request.query,
      item: selectedItem,
      position,
      listType,
    };

    // fire event for external listeners
    this.onSearchItemSelectEvent.emit(data);

    // track click in searchclient if it supports this
    return this.searchClient.trackResultItemClick(queryID, selectedItem, position, listType);
  }

  /**
   * check if input field min length is reached.
   */
  hasMinSearchValueLength(): boolean {
    return (
      this.searchInput &&
      this.searchInput['value'] &&
      this.searchInput['value'].length >= this.configuration.minSearchPhraseLength
    );
  }

  /**
   * scroll the search overlay up
   * @param behavior
   * @private
   */
  private scrollToTop(behavior: ScrollBehavior) {
    const isDesktop = this.breakPoints.breakpointUp('md');
    const scrollContent = this.getContent().querySelector(isDesktop ? 'gridcontainer' : 'overlay-content');
    scrollContent?.scrollTo({ top: 0, behavior: behavior });
  }

  /**
   * scroll to top if the search results have changed
   * @param oldSearchResponse
   * @param newSearchResponse
   * @private
   */
  private scrollOnResultChanges() {
    if (!this.searchResponse.equals(this.oldSearchResponseID)) {
      this.scrollToTop('smooth');
      this.oldSearchResponseID = this.searchResponse?.getCompoundID();
    }
  }

  /**
   * change the state to open.
   * @private
   */
  private open() {
    if (this.state != OnsiteSearchState.Opened) {
      this.onSearchShowEvent.emit(this.element);
    }

    document.querySelector('body').style.overflow = 'hidden';

    this.state = OnsiteSearchState.Opened;
  }

  /**
   * implements keyboard selection of results.
   * @param inputs
   */
  // @TODO: maybe this can be solved by @Listen decorators
  private enableKeyboardSelectNextItem(inputs) {
    for (let i = 0; i < inputs.length; i++) {
      inputs[i].onkeydown = function (ev) {
        if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
          let node = this.nextSibling;
          if (ev.key === 'ArrowUp') {
            node = this.previousSibling;
          }
          if (node.tagName == 'result-item' || node.tagName == 'RESULT-ITEM') {
            node.focus();
          }
        } else if (ev.key === 'Enter') {
          if (this.tagName == 'result-item' || this.tagName == 'RESULT-ITEM') {
            this.click();
          }
        }
      };
    }
  }

  /**
   * show loading animation delayed.
   * @param delay
   */
  private showLoading(delay: number = 1) {
    this.loadingTimer = setTimeout(() => {
      this.loading = true;
      this.open();
    }, delay);
  }

  /**
   * abort loading animation.
   */
  private hideLoading() {
    this.loading = false;
    clearTimeout(this.loadingTimer);
  }

  /**
   * track the result-set via datalayer.
   * @private
   */
  private trackResults(searchResponse: SearchResponse, searchQueryType: SearchQueryType) {
    /**
     * @TODO:
     * Add other list types not just product
     */

    clearTimeout(this.trackingTimer);
    this.delayTimeCalculator.reset();

    if (this.searchInput['value'].length < 3) {
      return;
    }

    if (Object.keys(searchResponse.results).length === 0) {
      // track empty searchresults
      const data: TrackingQuery = { query: this.originalSearchQuery };
      this.onSearchResultsNotFoundEvent.emit(data);
    } else {
      // track default results
      const stats: TrackingResultStats = {};
      for (const schema in SchemaName) {
        stats[schema as keyof typeof SchemaName] = {
          count: searchResponse.results[schema as keyof typeof SchemaName]?.hits.length ?? 0,
        };
      }
      const data: TrackingResult = {
        flag: searchQueryType,
        query: this.originalSearchQuery,
        stats,
      };
      this.onSearchResultsFoundEvent.emit(data);
    }
  }

  /**
   * process once information from session state like last search query
   * @private
   */
  private handleSessionState() {
    if (!this.sessionStateHandled) {
      this.loadLastSearch();
      this.sessionStateHandled = true;
    }
  }

  /**
   * load the last search query and open search in mobile if user uses history back.
   * @private
   */
  private loadLastSearch() {
    const params = new URLSearchParams(window.location.search);
    const perfNav = performance.getEntriesByType('navigation');
    const usedHistoryBack = perfNav[0] ? (perfNav[0] as PerformanceNavigationTiming).type === 'back_forward' : false;

    if (sessionStore.lastSearchQuery && sessionStore.lastSearchQuery != '') {
      if (params.has(OnsiteSearchComponent.referrerMarker)) {
        this.setInputValue(sessionStore.lastSearchQuery);
      } else if (usedHistoryBack) {
        this.setInputValue(sessionStore.lastSearchQuery);
        if (this.breakPoints.breakpointDown('sm')) {
          this.search(sessionStore.lastSearchQuery).then();
        }
      }
    }
  }

  /**
   * set the value of input field, try to use setValue method.
   * @param value
   * @private
   */
  private setInputValue(value: string) {
    if (!this.searchInput) {
      return;
    }

    switch (this.searchInput.constructor.name) {
      case 'HTMLInputElement':
        (this.searchInput as HTMLInputElement).value = value;
        break;
      default:
        switch (this.searchInput.tagName) {
          case 'CWC-COMPONENT-AUTOCOMPLETE':
            // @ts-expect-error migrated from ts-ignore
            this.searchInput.setValue(value);
            break;
        }
        break;
    }
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(OnsiteSearchComponent.prototype, AbstractComponent.prototype);

export interface OnsiteSearchComponent extends AbstractComponent {}
